// The host-audio capture session (TT-1102): this tab is the keeper.
//
// One module-level session — a capture outlives the modal that started it
// and the page that shows it, so it cannot live in component state. The
// flow mirrors keeper/src/transcription.js: audio → Transcribe streaming →
// each result dripped as its sentences turn stable (stableDrips.js, TT-1103),
// each sentence under a pending speaker the FINAL then names from that
// sentence's own labels, the FINAL carrying the rest split into speaker-pure
// pieces — as is a result dripped only at its FINAL, one drip per piece. Drips post to appback, which publishes them onto the
// docdrip topic. From there the spine cannot tell this tapestry from a
// bot-captured one.
//
//   acquireStream()  — inside the user's click (getUserMedia / getDisplayMedia)
//   start()          — doc exists; wire audio → worklet → Transcribe → drips
//   stop()           — end the STT session, flush its last FINAL, close drip
//
// When the doc retains audio (TT-1190), the same frames are also teed into
// 60 s WAV segments uploaded to the source-audio bucket (segmentTee.js).
//
// Subscribers get a snapshot on every change (see useHostCapture).

import AppBackService from "../../services/appback";
import { attributeItems, speakerName } from "./speakerRuns";
import { createResultCursor, pendingSpeaker } from "./stableDrips";
import { openTranscribeSession } from "./transcribeSession";
import { pcmWorkletUrl, TARGET_SAMPLE_RATE } from "./pcmWorklet";
import { createSegmentTee } from "./segmentTee";

export const CAPTURE_STATE = {
  IDLE: "idle",
  STARTING: "starting",
  RUNNING: "running",
  RECONNECTING: "reconnecting",
  STOPPING: "stopping",
  STOPPED: "stopped",
  ERROR: "error",
};

export const SOURCE_KIND = { MIC: "mic", SHARE: "share" };

// Frames buffered while the socket is (re)connecting; older ones are dropped.
const MAX_BUFFERED_SECONDS = 30;
const MAX_RECONNECTS = 5;
const DRIP_RETRIES = 3;
const STOP_FLUSH_MS = 2500;
// The keepalive close on unload needs a bearer that is still valid; Cognito
// access tokens live an hour, so refresh the cached one well inside that.
const BEARER_REFRESH_MS = 5 * 60 * 1000;
const BYTES_PER_SECOND = TARGET_SAMPLE_RATE * 2;

const ACTIVE_STATES = new Set([CAPTURE_STATE.STARTING, CAPTURE_STATE.RUNNING, CAPTURE_STATE.RECONNECTING]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Several pieces as one span of text (the label they carry is not what names
// them — the block's name comes from the FINAL).
function joinPieces(pieces) {
  const withText = pieces.filter((p) => p.text);
  if (!withText.length) return null;
  return {
    speaker: withText[0].speaker,
    text: withText.map((p) => p.text).join(" "),
    start: withText[0].start,
    end: withText[withText.length - 1].end,
  };
}

// Grab the audio inside the click that asked for it — both prompts need the
// user's activation, and an awaited network call in between can lose it.
export async function acquireStream({ kind, deviceId }) {
  if (kind === SOURCE_KIND.SHARE) {
    // Chrome requires video on a display capture; we keep only the audio.
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      systemAudio: "include",
      selfBrowserSurface: "exclude",
    });
    stream.getVideoTracks().forEach((t) => t.stop());
    if (!stream.getAudioTracks().length) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error("No audio was shared — tick “Share audio” in the picker and try again.");
    }
    return stream;
  }
  const audio = deviceId ? { deviceId: { exact: deviceId } } : true;
  return navigator.mediaDevices.getUserMedia({ audio, video: false });
}

export async function listMicrophones() {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === "audioinput");
}

class HostCapture {
  constructor() {
    this.listeners = new Set();
    this.reset();
  }

  reset() {
    this.state = CAPTURE_STATE.IDLE;
    this.userId = null;
    this.docId = null;
    this.sourceLabel = "";
    this.sourceKind = null;
    this.startedAt = null;
    this.muted = false;
    this.level = 0;
    this.error = null;
    this.dripCount = 0;
    this.dripNum = 0;
    this.reconnects = 0;
    this.currentSpeaker = null;
    this.cursor = createResultCursor();
    this.lastText = "";
    this.stream = null;
    this.audioContext = null;
    this.workletNode = null;
    this.session = null;
    this.buffered = [];
    this.bufferedBytes = 0;
    this.sentBytes = 0;
    this.sessionOffsetSec = 0;
    this.bearer = null;
    this.bearerTimer = null;
    this.dripQueue = Promise.resolve();
    // drips numbered but not yet acknowledged — sent keepalive on unload
    this.pendingDrips = [];
    this.stopRequested = false;
    this.dismissed = false;
    // the retained-audio tee, only for a doc whose start answered retain_audio: true
    this.tee = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot() {
    return {
      state: this.state,
      active: ACTIVE_STATES.has(this.state),
      userId: this.userId,
      docId: this.docId,
      sourceLabel: this.sourceLabel,
      sourceKind: this.sourceKind,
      startedAt: this.startedAt,
      muted: this.muted,
      level: this.level,
      error: this.error,
      dripCount: this.dripCount,
      reconnects: this.reconnects,
      lastText: this.lastText,
      dismissed: this.dismissed,
    };
  }

  // The bar's dismiss lives here, not in the component: a STOPPED/ERROR
  // capture stays on the singleton until the next start(), and the bar must
  // not come back on every visit to that doc.
  dismiss() {
    this.dismissed = true;
    this.emit();
  }

  emit() {
    const snap = this.snapshot();
    this.listeners.forEach((fn) => fn(snap));
  }

  setState(state, patch = {}) {
    this.state = state;
    Object.assign(this, patch);
    this.emit();
  }

  isCapturing(docId) {
    return ACTIVE_STATES.has(this.state) && (!docId || this.docId === docId);
  }

  async start({ userId, docId, stream, sourceLabel, sourceKind, retainAudio = false }) {
    if (ACTIVE_STATES.has(this.state)) {
      throw new Error("A capture is already running");
    }
    this.reset();
    this.userId = userId;
    this.docId = docId;
    this.stream = stream;
    this.sourceLabel = sourceLabel;
    this.sourceKind = sourceKind;
    this.startedAt = Date.now();
    this.tee = retainAudio ? createSegmentTee({ userId, docId, getBearer: () => this.bearer }) : null;
    this.setState(CAPTURE_STATE.STARTING);

    try {
      await this.wireAudio(stream);
      await this.connect();
      this.installUnloadGuards();
      this.bearerTimer = setInterval(() => this.refreshBearer(), BEARER_REFRESH_MS);
    } catch (err) {
      // before teardown: a socket close landing during the (async) teardown
      // must not read as a drop to reconnect from
      this.stopRequested = true;
      this.session?.close();
      this.tee?.abandon();
      this.tee = null;
      await this.teardownAudio();
      this.setState(CAPTURE_STATE.ERROR, { error: err.message || String(err) });
      throw err;
    }
  }

  async refreshBearer() {
    const bearer = await AppBackService.currentBearer().catch(() => null);
    if (bearer) this.bearer = bearer;
  }

  async wireAudio(stream) {
    const ctx = new AudioContext();
    await ctx.audioWorklet.addModule(pcmWorkletUrl());
    const source = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, "pcm-capture", { numberOfInputs: 1, numberOfOutputs: 0 });
    node.port.onmessage = (e) => this.onWorkletMessage(e.data);
    source.connect(node);
    if (ctx.state === "suspended") await ctx.resume();
    this.audioContext = ctx;
    this.workletNode = node;

    // The user hit "Stop sharing" in Chrome's bar, or unplugged the mic.
    stream.getAudioTracks().forEach((track) => {
      track.addEventListener("ended", () => {
        if (ACTIVE_STATES.has(this.state)) this.stop();
      });
    });
  }

  onWorkletMessage(data) {
    if (!data) return;
    if (data.type === "level") {
      this.level = data.rms;
      this.emit();
      return;
    }
    if (data.type !== "pcm") return;
    const frame = new Uint8Array(data.frame);
    // every frame, through reconnects too: the retained audio has no gap
    // where Transcribe had one
    if (this.tee && ACTIVE_STATES.has(this.state)) this.tee.push(frame);
    if (this.session?.open && this.state === CAPTURE_STATE.RUNNING) {
      this.session.send(frame);
      this.sentBytes += frame.length;
      return;
    }
    if (!ACTIVE_STATES.has(this.state)) return;
    this.buffered.push(frame);
    this.bufferedBytes += frame.length;
    while (this.bufferedBytes > MAX_BUFFERED_SECONDS * BYTES_PER_SECOND && this.buffered.length) {
      this.bufferedBytes -= this.buffered.shift().length;
    }
  }

  flushBuffered() {
    const frames = this.buffered;
    this.buffered = [];
    this.bufferedBytes = 0;
    frames.forEach((frame) => {
      this.session.send(frame);
      this.sentBytes += frame.length;
    });
  }

  // One Transcribe session. Resolves once the socket is open; a close that
  // wasn't ours triggers a reconnect (fresh presigned URL) unless we're out.
  async connect() {
    const resp = await AppBackService.createSttSession(this.userId, this.docId);
    if (this.stopRequested) return;
    if (!resp || resp.error || !resp.wsUrl) {
      throw new Error(resp?.msg || "Could not start the transcription session");
    }
    await this.refreshBearer();
    if (this.stopRequested) return;

    // fresh session, fresh result ids; a result cut off by a drop is lost
    // past its last dripped sentence, as its FINAL would have been
    this.cursor.reset();
    await new Promise((resolve, reject) => {
      let settled = false;
      let opened = false;
      const session = openTranscribeSession({
        wsUrl: resp.wsUrl,
        onOpen: () => {
          settled = true;
          if (this.stopRequested) {
            // stop() won the race while this socket was opening
            session.close();
            resolve();
            return;
          }
          opened = true;
          this.session = session;
          this.setState(CAPTURE_STATE.RUNNING, { error: null });
          this.flushBuffered();
          resolve();
        },
        onResult: (result) => this.onResult(result),
        onError: (err) => {
          console.warn("Transcribe session error:", err.message);
          if (!settled) {
            settled = true;
            reject(err);
          }
        },
        onClose: (info) => {
          if (!settled) {
            settled = true;
            reject(new Error(`Transcribe refused the session (${info.code})`));
            return;
          }
          // a socket that never opened has nothing to reconnect from
          if (opened) this.onSessionClosed(info);
        },
      });
    });
  }

  async onSessionClosed(info) {
    if (this.stopRequested || !ACTIVE_STATES.has(this.state)) return;
    if (this.reconnects >= MAX_RECONNECTS) {
      this.fail(`Transcription dropped and could not be restored (${info.code})`);
      return;
    }
    // Timestamps restart at 0 on a new session; carry the media clock over.
    this.sessionOffsetSec += this.sentBytes / BYTES_PER_SECOND;
    this.sentBytes = 0;
    this.reconnects += 1;
    this.setState(CAPTURE_STATE.RECONNECTING);
    await sleep(Math.min(500 * 2 ** this.reconnects, 8000));
    if (this.stopRequested) return;
    try {
      await this.connect();
    } catch (err) {
      this.onSessionClosed({ code: "retry" });
    }
  }

  // One cursor take → drips. Three shapes (see stableDrips.js): early
  // sentences, each under a pending block of its own; the FINAL that names
  // those blocks and drips the rest split at its labels; or a plain labeled
  // result.
  onResult(result) {
    const take = this.cursor.take(result);
    if (take.pending.length) {
      take.pending.forEach((sentence) => this.dripPending(take.resultId, sentence));
      this.emit();
      return;
    }
    const pieces = attributeItems(take.items);
    let speakers;
    if (take.blocks.length) {
      // the FINAL of a result dripped early: each block named by its own
      // words' labels; the rest continues the last block when its speaker
      // is the same, else opens a run of its own — a result can hold
      // several speakers, so it is never one speaker's
      speakers = Object.fromEntries(take.blocks.map(([num, label]) => [String(num), speakerName(label)]));
      this.currentSpeaker = speakerName(take.blocks[take.blocks.length - 1][1]);
      if (!pieces.length) {
        this.enqueueDrip({ content: "", speakers });
        this.emit();
        return;
      }
    }
    pieces.forEach((piece, i) => {
      let content = "";
      if (piece.speaker !== this.currentSpeaker) {
        content += `${piece.speaker}:\n\n`;
        this.currentSpeaker = piece.speaker;
      }
      content += `${piece.text}\n\n`;
      this.lastText = piece.text;
      this.enqueueDrip({ content, utterances: this.utterancesFor(piece), speakers: i === 0 ? speakers : undefined });
    });
    if (pieces.length) this.emit();
  }

  // One early sentence under a pending block its drip's number names; its
  // utterance carries that name too, so the FINAL's naming reaches the timed
  // words as well as the text.
  dripPending(resultId, items) {
    // unlabeled today, so one piece; joined in case labels ever come
    const joined = joinPieces(attributeItems(items));
    if (!joined) return;
    const num = this.dripNum + 1;
    const pending = pendingSpeaker(num);
    const piece = { ...joined, speaker: pending };
    this.lastText = piece.text;
    this.currentSpeaker = pending;
    this.enqueueDrip({ content: `${pending}:\n\n${piece.text}\n\n`, utterances: this.utterancesFor(piece) });
    this.cursor.opened(resultId, num);
  }

  utterancesFor(piece) {
    if (!piece.text) return undefined;
    return [
      {
        speaker: piece.speaker,
        text: piece.text,
        t0: piece.start == null ? null : piece.start + this.sessionOffsetSec,
        t1: piece.end == null ? null : piece.end + this.sessionOffsetSec,
      },
    ];
  }

  // Drips post serially in the order they were numbered (the spine reads
  // by drip number and tolerates a gap, but a skipped drip is lost words);
  // a failed post is retried a few times before the capture gives up.
  enqueueDrip({ content, utterances, speakers, close = false }) {
    this.dripNum += 1;
    const drip = { drip_num: this.dripNum, content, close };
    if (utterances) drip.utterances = utterances;
    if (speakers) drip.speakers = speakers;
    this.pendingDrips.push(drip);
    this.dripQueue = this.dripQueue.then(() => this.postDrip(drip));
    return this.dripQueue;
  }

  async postDrip(drip) {
    try {
      for (let attempt = 1; attempt <= DRIP_RETRIES; attempt += 1) {
        const resp = await AppBackService.postDrip(this.userId, this.docId, drip);
        if (resp && !resp.error) {
          this.dripCount += 1;
          this.emit();
          return resp;
        }
        // A closed doc will never take another drip; stop retrying.
        if (resp?.status === 409) return resp;
        await sleep(400 * attempt);
      }
      this.fail(`Could not deliver drip ${drip.drip_num}`);
      return null;
    } finally {
      this.pendingDrips = this.pendingDrips.filter((d) => d !== drip);
    }
  }

  async stop() {
    if (!ACTIVE_STATES.has(this.state)) return;
    this.stopRequested = true;
    this.setState(CAPTURE_STATE.STOPPING);
    this.removeUnloadGuards();
    clearInterval(this.bearerTimer);

    // End the stream politely so Transcribe emits the last FINAL, then wait
    // (briefly) for it to close from its side before closing the drips.
    const session = this.session;
    if (session) {
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, STOP_FLUSH_MS);
        const poll = setInterval(() => {
          if (!session.open) {
            clearTimeout(timer);
            clearInterval(poll);
            resolve();
          }
        }, 100);
        session.end();
      });
      session.close();
    }
    await this.teardownAudio();
    await this.enqueueDrip({ content: "", close: true }).catch(() => null);
    // not awaited: the last segment's upload and the set's close follow the
    // close drip (the segment routes take a closed doc) and must not hold Stop
    this.tee?.finish();
    this.setState(CAPTURE_STATE.STOPPED);
  }

  fail(message) {
    console.error("Host capture failed:", message);
    this.stopRequested = true;
    this.removeUnloadGuards();
    clearInterval(this.bearerTimer);
    this.session?.close();
    this.teardownAudio();
    this.tee?.finish(); // keep what was captured
    this.setState(CAPTURE_STATE.ERROR, { error: message });
  }

  async teardownAudio() {
    try {
      this.workletNode?.disconnect();
      this.stream?.getTracks().forEach((t) => t.stop());
      if (this.audioContext && this.audioContext.state !== "closed") await this.audioContext.close();
    } catch (e) {
      // best effort
    }
    this.workletNode = null;
    this.audioContext = null;
  }

  setMuted(muted) {
    this.muted = !!muted;
    this.workletNode?.port.postMessage({ type: "mute", muted: this.muted });
    this.emit();
  }

  elapsedSeconds() {
    return this.startedAt ? Math.floor((Date.now() - this.startedAt) / 1000) : 0;
  }

  // Leaving the page ends the tapestry: warn first, then close it on the way
  // out with a keepalive request the browser lets outlive the page.
  installUnloadGuards() {
    this.onBeforeUnload = (e) => {
      if (!ACTIVE_STATES.has(this.state)) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    this.onPageHide = () => {
      if (!ACTIVE_STATES.has(this.state)) return;
      this.stopRequested = true;
      clearInterval(this.bearerTimer);
      this.session?.close();
      this.teardownAudio();
      // Whatever axios still had in hand goes out again keepalive — the
      // server dedupes by drip number — then the close. No Transcribe drain:
      // a FINAL still inside Transcribe when the tab goes is lost.
      this.pendingDrips.forEach((drip) => AppBackService.postDripKeepalive(this.userId, this.docId, drip, this.bearer));
      this.dripNum += 1;
      AppBackService.postDripKeepalive(
        this.userId,
        this.docId,
        { drip_num: this.dripNum, content: "", close: true },
        this.bearer
      );
      this.tee?.closeOnUnload(this.bearer);
      this.state = CAPTURE_STATE.STOPPED;
    };
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("pagehide", this.onPageHide);
  }

  removeUnloadGuards() {
    if (this.onBeforeUnload) window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.onPageHide) window.removeEventListener("pagehide", this.onPageHide);
    this.onBeforeUnload = null;
    this.onPageHide = null;
  }
}

const hostCapture = new HostCapture();

export default hostCapture;
